'use client'

import { MarketAssetStatus } from '@/domain/market-info/reserve-status'
import { Panel } from '@/ui/atoms/panel/Panel'
import { Address } from 'viem'
import { useQuery } from '@tanstack/react-query'
import { Typography } from '@/ui/atoms/typography/Typography'
import { useMemo } from 'react'
import { useParentSize } from '@/ui/utils/useParentSize'
import { API_INDEXER_URL } from '@/config/consts'
import {
  ChartLegend,
  ChartSkeleton,
} from '@/features/isolated-details/components/historic-interest-rate/components/ChartLegend'
import {
  APYChartWithTooltip,
  getPanelTitle,
} from '@/features/isolated-details/components/historic-interest-rate/components/APYChart'

interface HistoricInterestRatePanelProps {
  borrowStatus: MarketAssetStatus
  supplyStatus: MarketAssetStatus
  chainId: number
  tokenAddress: Address
  description?: string
  docsLink?: string
}

interface TimeSeriesDataPoint {
  timestamp: number
  date: string
  borrowAPY: number
  lendAPY: number
  updateCount: number
}

interface HistoricInterestRateResponse {
  tokenAddress: string
  timeSeriesData: TimeSeriesDataPoint[]
}

async function getHistoricInterestRate(chainId: number, address: Address) {
  const response = await fetch(`${API_INDEXER_URL}/api/chains/${chainId}/pooled/${address}/apy`)
  const data = await response.json()

  return data
}

export function HistoricInterestRatePanel({
  borrowStatus,
  supplyStatus,
  chainId,
  tokenAddress,
  description,
  docsLink,
}: HistoricInterestRatePanelProps) {
  const [ref, { width }] = useParentSize()

  const { data: historicInterestRate, isLoading } = useQuery<HistoricInterestRateResponse>({
    queryKey: ['pooled-historic-interest-rate', chainId, tokenAddress],
    queryFn: () => getHistoricInterestRate(chainId, tokenAddress),
    enabled: !!tokenAddress && !!chainId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: 1000 * 60 * 5,
    refetchInterval: 1000 * 60 * 5,
  })

  const chartData = useMemo(() => {
    if (!historicInterestRate?.timeSeriesData?.length) return []

    const sortedData = [...historicInterestRate.timeSeriesData]
      .sort((a, b) => a.timestamp - b.timestamp)
      .map((item) => ({
        date: new Date(item.timestamp),
        borrowAPY: item.borrowAPY,
        lendAPY: item.lendAPY,
      }))

    return sortedData
  }, [historicInterestRate])

  const panelTitle = getPanelTitle(borrowStatus, supplyStatus)

  return (
    <Panel.Wrapper>
      <div className="p-4 sm:p-8">
        <div className="flex w-full flex-col items-start gap-4">
          <Typography variant="h3" gradient className="text-lg">
            {panelTitle}
          </Typography>
          {description && (
            <Typography variant="p" className="font-medium text-muted-foreground text-sm">
              {description}
            </Typography>
          )}
        </div>
        <div className="col-span-3 mt-6 sm:mt-10" ref={ref}>
          {isLoading ? (
            <ChartSkeleton />
          ) : historicInterestRate && chartData.length > 0 ? (
            <div>
              <ChartLegend supplyStatus={supplyStatus} borrowStatus={borrowStatus} />
              <div style={{ height: '180px', position: 'relative' }}>
                <APYChartWithTooltip
                  supplyStatus={supplyStatus}
                  borrowStatus={borrowStatus}
                  width={width || 500}
                  height={180}
                  data={chartData}
                  showTime={true}
                  showBorrowAvg={borrowStatus !== 'no'}
                  showLendAvg={supplyStatus !== 'no'}
                />
              </div>
            </div>
          ) : (
            <Typography variant="prompt">No historical data available</Typography>
          )}
        </div>

        {docsLink && (
          <a
            href={docsLink}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 inline-block text-primary text-sm transition hover:opacity-80"
          >
            Read more in the docs...
          </a>
        )}
      </div>
    </Panel.Wrapper>
  )
}
